import React, { useEffect, useMemo, useState } from 'react';
import { Mic2, PhoneCall } from 'lucide-react';
import { apiClient } from '@/lib/api';
import type {
  AgentConfiguration,
  AgentVoiceConfig,
  RealtimeVoicePolicy,
  TTSVoicePolicy,
  AudioSettings,
} from '@/types';

interface VoiceSectionProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
}

const PRODUCT_AGENT_TYPES = new Set(['dora', 'soulmate']);

const productStrategy = (agentType?: string): 'fixed' | 'soulmate_profile' =>
  agentType === 'soulmate' ? 'soulmate_profile' : 'fixed';

const firstKey = <T extends Record<string, unknown>>(value: T): keyof T & string =>
  Object.keys(value)[0] as keyof T & string;

const defaultVoice = (inventory: AudioSettings, agentType?: string): AgentVoiceConfig => {
  const ttsProvider = firstKey(inventory.tts_providers);
  const realtimeProvider = firstKey(inventory.realtime_providers);
  const ttsMeta = inventory.tts_providers[ttsProvider];
  const realtimeMeta = inventory.realtime_providers[realtimeProvider];
  const strategy = productStrategy(agentType);
  return {
    tts: {
      enabled: true,
      provider: ttsProvider as TTSVoicePolicy['provider'],
      model: ttsMeta.models[0]?.id || '',
      voice_strategy: strategy,
      voice_id: strategy === 'fixed' ? ttsMeta.voices[0]?.id || null : null,
      speed: 1,
      streaming_enabled: ttsMeta.supports_streaming,
      preamble_trim_ms: 600,
    },
    realtime: {
      enabled: true,
      provider: realtimeProvider as RealtimeVoicePolicy['provider'],
      model: realtimeMeta.models[0]?.id || '',
      voice_strategy: strategy,
      voice_id: strategy === 'fixed' ? realtimeMeta.voices[0]?.id || null : null,
      vad_mode: (realtimeMeta.models[0]?.vad_modes[0] || 'server_vad') as RealtimeVoicePolicy['vad_mode'],
      interrupt_enabled: realtimeMeta.models[0]?.supports_interrupt ?? true,
      reasoning_effort: 'low',
    },
  };
};

export const VoiceSection: React.FC<VoiceSectionProps> = ({ config, onChange }) => {
  const [inventory, setInventory] = useState<AudioSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const agentType = config.agent_type;
  const strategy = productStrategy(agentType);
  const voice = config.voice || null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<AudioSettings>('/admin/audio/config')
      .then((response) => {
        if (!cancelled) {
          setInventory(response.data);
          setError(null);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message || '加载音频库存失败');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizedVoice = useMemo(() => {
    if (!inventory) return voice;
    if (!voice) return null;
    if (voice.tts.voice_strategy === strategy && voice.realtime.voice_strategy === strategy) return voice;
    return {
      tts: { ...voice.tts, voice_strategy: strategy, voice_id: strategy === 'fixed' ? voice.tts.voice_id : null },
      realtime: {
        ...voice.realtime,
        voice_strategy: strategy,
        voice_id: strategy === 'fixed' ? voice.realtime.voice_id : null,
      },
    };
  }, [inventory, strategy, voice]);

  if (!agentType || !PRODUCT_AGENT_TYPES.has(agentType)) {
    return null;
  }

  const setVoice = (next: AgentVoiceConfig) => {
    onChange('voice', next);
  };

  const updateTts = (patch: Partial<TTSVoicePolicy>) => {
    if (!normalizedVoice) return;
    setVoice({ ...normalizedVoice, tts: { ...normalizedVoice.tts, ...patch, voice_strategy: strategy } });
  };

  const updateRealtime = (patch: Partial<RealtimeVoicePolicy>) => {
    if (!normalizedVoice) return;
    setVoice({
      ...normalizedVoice,
      realtime: { ...normalizedVoice.realtime, ...patch, voice_strategy: strategy },
    });
  };

  const ttsProviderMeta = normalizedVoice && inventory?.tts_providers[normalizedVoice.tts.provider];
  const realtimeProviderMeta = normalizedVoice && inventory?.realtime_providers[normalizedVoice.realtime.provider];
  const realtimeModelMeta = realtimeProviderMeta?.models.find((model) => model.id === normalizedVoice?.realtime.model);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">语音策略</h3>
          <p className="mt-1 text-sm text-slate-500">
            当前 Agent 独立配置 TTS 和实时语音。Dora 固定音色；Soulmate 按人格 Artifact 注入稳定音色。
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {agentType === 'soulmate' ? 'Soulmate profile' : 'Dora fixed'}
        </span>
      </div>

      {loading && <div className="mt-4 text-sm text-slate-500">加载音频库存...</div>}
      {error && <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</div>}

      {!loading && inventory && !normalizedVoice && (
        <button
          type="button"
          className="mt-4 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => setVoice(defaultVoice(inventory, agentType))}
        >
          初始化 {agentType} 语音策略
        </button>
      )}

      {!loading && inventory && normalizedVoice && (
        <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
            <div className="mb-4 flex items-center gap-2">
              <Mic2 className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-800">TTS / 文字转语音</h4>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="space-y-1 text-xs font-medium text-slate-600">
                服务商
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={normalizedVoice.tts.provider}
                  onChange={(event) => {
                    const provider = event.target.value as TTSVoicePolicy['provider'];
                    const meta = inventory.tts_providers[provider];
                    updateTts({
                      provider,
                      model: meta.models[0]?.id || '',
                      voice_id: strategy === 'fixed' ? meta.voices[0]?.id || null : null,
                      streaming_enabled: meta.supports_streaming,
                    });
                  }}
                >
                  {Object.entries(inventory.tts_providers).map(([provider, meta]) => (
                    <option key={provider} value={provider}>
                      {meta.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                模型
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={normalizedVoice.tts.model}
                  onChange={(event) => updateTts({ model: event.target.value })}
                >
                  {(ttsProviderMeta?.models || []).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                音色
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                  value={normalizedVoice.tts.voice_id || ''}
                  disabled={strategy !== 'fixed'}
                  onChange={(event) => updateTts({ voice_id: event.target.value || null })}
                >
                  {strategy !== 'fixed' && <option value="">由 Soulmate 人格匹配</option>}
                  {(ttsProviderMeta?.voices || []).map((voiceOption) => (
                    <option key={voiceOption.id} value={voiceOption.id}>
                      {voiceOption.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                语速：{normalizedVoice.tts.speed.toFixed(1)}
                <input
                  className="w-full"
                  type="range"
                  min={ttsProviderMeta?.speed_range[0] ?? 0.5}
                  max={ttsProviderMeta?.speed_range[1] ?? 2}
                  step={0.1}
                  value={normalizedVoice.tts.speed}
                  onChange={(event) => updateTts({ speed: parseFloat(event.target.value) })}
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
            <div className="mb-4 flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-800">Realtime / 实时语音</h4>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="space-y-1 text-xs font-medium text-slate-600">
                服务商
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={normalizedVoice.realtime.provider}
                  onChange={(event) => {
                    const provider = event.target.value as RealtimeVoicePolicy['provider'];
                    const meta = inventory.realtime_providers[provider];
                    const model = meta.models[0];
                    updateRealtime({
                      provider,
                      model: model?.id || '',
                      voice_id: strategy === 'fixed' ? meta.voices[0]?.id || null : null,
                      vad_mode: (model?.vad_modes[0] || 'server_vad') as RealtimeVoicePolicy['vad_mode'],
                      interrupt_enabled: model?.supports_interrupt ?? true,
                    });
                  }}
                >
                  {Object.entries(inventory.realtime_providers).map(([provider, meta]) => (
                    <option key={provider} value={provider}>
                      {meta.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                模型
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={normalizedVoice.realtime.model}
                  onChange={(event) => {
                    const model = realtimeProviderMeta?.models.find((item) => item.id === event.target.value);
                    updateRealtime({
                      model: event.target.value,
                      vad_mode: (model?.vad_modes[0] || 'server_vad') as RealtimeVoicePolicy['vad_mode'],
                      interrupt_enabled: model?.supports_interrupt ?? true,
                    });
                  }}
                >
                  {(realtimeProviderMeta?.models || []).map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                音色
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm disabled:bg-slate-100"
                  value={normalizedVoice.realtime.voice_id || ''}
                  disabled={strategy !== 'fixed'}
                  onChange={(event) => updateRealtime({ voice_id: event.target.value || null })}
                >
                  {strategy !== 'fixed' && <option value="">由 Soulmate 人格匹配</option>}
                  {(realtimeProviderMeta?.voices || []).map((voiceOption) => (
                    <option key={voiceOption.id} value={voiceOption.id}>
                      {voiceOption.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="space-y-1 text-xs font-medium text-slate-600">
                断句 / VAD
                <select
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={normalizedVoice.realtime.vad_mode}
                  onChange={(event) =>
                    updateRealtime({ vad_mode: event.target.value as RealtimeVoicePolicy['vad_mode'] })
                  }
                >
                  {(realtimeModelMeta?.vad_modes || ['server_vad']).map((mode) => (
                    <option key={mode} value={mode}>
                      {mode}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={normalizedVoice.realtime.interrupt_enabled}
                  onChange={(event) => updateRealtime({ interrupt_enabled: event.target.checked })}
                />
                允许用户打断
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                <input
                  type="checkbox"
                  checked={normalizedVoice.realtime.enabled}
                  onChange={(event) => updateRealtime({ enabled: event.target.checked })}
                />
                启用实时语音
              </label>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VoiceSection;
